/** Formatting helpers. Money is integer cents everywhere (SPEC §0.5). */

const usd0 = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const usd2 = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
const int = new Intl.NumberFormat('en-US');

/** $1,234.56 — for balances, fees and ledger rows. */
export function money(cents: number | null | undefined): string {
  if (cents == null || Number.isNaN(cents)) return '—';
  return usd2.format(cents / 100);
}
/** $2,850,000 — for asking prices (whole dollars). */
export function price(cents: number | null | undefined, onRequest = false): string {
  if (onRequest) return 'Price on request';
  if (cents == null) return '—';
  return usd0.format(cents / 100);
}
/** $2.85M / $389k — compact. */
export function priceShort(cents: number | null | undefined): string {
  if (cents == null) return '—';
  const d = cents / 100;
  if (d >= 1_000_000) return `$${(d / 1_000_000).toFixed(d >= 10_000_000 ? 1 : 2).replace(/\.?0+$/, '')}M`;
  if (d >= 10_000) return `$${Math.round(d / 1000)}k`;
  return usd0.format(d);
}
export function num(n: number | null | undefined, digits = 0): string {
  if (n == null || Number.isNaN(n)) return '—';
  return digits ? n.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits }) : int.format(Math.round(n));
}
export function compact(n: number | null | undefined): string {
  if (n == null) return '—';
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (Math.abs(n) >= 10_000) return `${Math.round(n / 1000)}k`;
  if (Math.abs(n) >= 1000) return `${(n / 1000).toFixed(1).replace(/\.0$/, '')}k`;
  return int.format(n);
}
export function pct(n: number | null | undefined, digits = 1): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return `${(n * 100).toFixed(digits)}%`;
}
/** Parse "$1,250.50" / "1250.5" → cents (null when blank/invalid). */
export function parseMoney(s: string): number | null {
  const t = s.replace(/[$,\s]/g, '');
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? Math.round(n * 100) : null;
}
export function centsToInput(c: number | null | undefined): string {
  if (c == null) return '';
  return (c / 100).toFixed(c % 100 === 0 ? 0 : 2);
}

const dtf = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
const dtfShort = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
const tf = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' });

export function date(iso: string | null | undefined): string {
  if (!iso) return '—';
  return dtf.format(new Date(iso));
}
export function dateShort(iso: string | null | undefined): string {
  if (!iso) return '—';
  return dtfShort.format(new Date(iso.length === 10 ? `${iso}T12:00:00Z` : iso));
}
export function dateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${dtf.format(d)}, ${tf.format(d)}`;
}
export function time(iso: string): string {
  return tf.format(new Date(iso));
}
/** "3m", "5h", "2d", "Mar 4" */
export function ago(iso: string | null | undefined): string {
  if (!iso) return '—';
  const s = (Date.now() - Date.parse(iso)) / 1000;
  if (s < 0) return inFuture(iso);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 7) return `${Math.floor(s / 86400)}d ago`;
  return dateShort(iso);
}
export function inFuture(iso: string): string {
  const s = (Date.parse(iso) - Date.now()) / 1000;
  if (s < 0) return ago(iso);
  if (s < 3600) return `in ${Math.max(1, Math.round(s / 60))}m`;
  if (s < 86400) return `in ${Math.round(s / 3600)}h`;
  return `in ${Math.round(s / 86400)}d`;
}
export function daysUntil(iso: string | null | undefined): number | null {
  if (!iso) return null;
  return Math.ceil((Date.parse(iso) - Date.now()) / 86400000);
}
/** YYYY-MM-DD in UTC, n days from today (negative = past). */
export function isoDay(offsetDays = 0): string {
  return new Date(Date.now() + offsetDays * 86400000).toISOString().slice(0, 10);
}

export function titleCase(s: string): string {
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
export function sentence(s: string): string {
  const t = s.replace(/_/g, ' ');
  return t.charAt(0).toUpperCase() + t.slice(1);
}
export function plural(n: number, one: string, many = `${one}s`): string {
  return `${num(n)} ${n === 1 ? one : many}`;
}
export function initials(name: string | null | undefined): string {
  if (!name) return '?';
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]!.toUpperCase()).join('');
}
